import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { useToast } from '@/hooks/use-toast';

interface CreateManualData {
  title: string;
  content: string;
  category: string;
  system_id?: string;
  is_published?: boolean;
  file_path?: string;
  mime_type?: string;
  file_size?: number;
}

interface UpdateManualData {
  title?: string;
  content?: string;
  category?: string;
  system_id?: string;
  is_published?: boolean;
  file_path?: string;
  mime_type?: string;
  file_size?: number;
}

export function useSupportManuals(systemId?: string) {
  return useQuery({
    queryKey: ['support-manuals', systemId],
    queryFn: async () => {
      let query = supabase
        .from('support_manuals')
        .select(`
          *,
          systems (
            id,
            name,
            code
          )
        `)
        .eq('is_published', true)
        .order('category')
        .order('created_at', { ascending: false });
      
      if (systemId) {
        query = query.eq('system_id', systemId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useAllSupportManuals() {
  return useQuery({
    queryKey: ['support-manuals-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_manuals')
        .select(`
          *,
          systems (
            id,
            name,
            code
          )
        `)
        .order('category')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useManualById(manualId: string) {
  return useQuery({
    queryKey: ['support-manual', manualId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_manuals')
        .select(`
          *,
          systems (
            id,
            name,
            code
          )
        `)
        .eq('id', manualId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!manualId,
  });
}

export function useCreateSupportManual() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateManualData) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('support_manuals')
        .insert({
          ...data,
          created_by: user.id,
          updated_by: user.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-manuals'] });
      queryClient.invalidateQueries({ queryKey: ['support-manuals-all'] });
      toast({
        title: "Manual criado",
        description: "Manual criado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar manual",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateSupportManual(manualId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: UpdateManualData) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('support_manuals')
        .update({
          ...data,
          updated_by: user.id,
        })
        .eq('id', manualId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-manuals'] });
      queryClient.invalidateQueries({ queryKey: ['support-manuals-all'] });
      queryClient.invalidateQueries({ queryKey: ['support-manual', manualId] });
      toast({
        title: "Manual atualizado",
        description: "Manual atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar manual",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteSupportManual() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (manualId: string) => {
      const { error } = await supabase
        .from('support_manuals')
        .delete()
        .eq('id', manualId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-manuals'] });
      queryClient.invalidateQueries({ queryKey: ['support-manuals-all'] });
      toast({
        title: "Manual excluído",
        description: "Manual excluído com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir manual",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
